import { EmailLinkErrorCodeStatus, isEmailLinkError } from '@clerk/shared/error';
import { completeSignUpFlow } from '@clerk/shared/internal/clerk-js/completeSignUpFlow';
import { getClerkQueryParam } from '@clerk/shared/internal/clerk-js/queryParams';
import { useClerk } from '@clerk/shared/react';
import React from 'react';

import { useCoreSignUp } from '../contexts';
import type { LocalizationKey } from '../localization';
import { useRouter } from '../router';
import { sleep } from '../utils/sleep';
import type { EmailLinkUIStatus } from './EmailLinkStatusCard';
import { EmailLinkStatusCard } from './EmailLinkStatusCard';

export type EmailLinkVerifyProps = {
  redirectUrlComplete?: string;
  redirectUrl?: string;
  ssoCallbackUrl?: string;
  oidcPrompt?: string;
  verifyEmailPath?: string;
  verifyPhonePath?: string;
  continuePath?: string;
  texts: Record<EmailLinkUIStatus, { title: LocalizationKey; subtitle: LocalizationKey }>;
};

export const EmailLinkVerify = (props: EmailLinkVerifyProps) => {
  const {
    redirectUrl,
    redirectUrlComplete,
    ssoCallbackUrl,
    oidcPrompt,
    verifyEmailPath,
    verifyPhonePath,
    continuePath,
  } = props;
  const { handleEmailLinkVerification } = useClerk();
  const { navigate } = useRouter();
  const signUp = useCoreSignUp();
  const [verificationStatus, setVerificationStatus] = React.useState<EmailLinkUIStatus>('loading');

  const startVerification = async () => {
    try {
      // Avoid loading flickering
      await sleep(750);
      await handleEmailLinkVerification({ redirectUrlComplete, redirectUrl }, navigate);

      // `transferable` = the email was verified but no user exists (`signUpIfMissing`).
      // The originating tab's poll performs the sign-up transfer, so this tab has no
      // session and nothing to complete - it only points the user back there.
      if (getClerkQueryParam('__clerk_status') === 'transferable') {
        setVerificationStatus('transferable');
        return;
      }

      setVerificationStatus('verified_switch_tab');
      await sleep(750);
      await completeSignUpFlow({
        signUp,
        verifyEmailPath,
        verifyPhonePath,
        protectCheckPath: '../protect-check',
        continuePath,
        navigate,
        redirectUrl: ssoCallbackUrl,
        redirectUrlComplete: redirectUrlComplete || '/',
        oidcPrompt,
      });
    } catch (err: any) {
      if (
        isEmailLinkError(err) &&
        (err.code === EmailLinkErrorCodeStatus.Expired || err.code === EmailLinkErrorCodeStatus.ClientMismatch)
      ) {
        setVerificationStatus(err.code);
        return;
      }

      setVerificationStatus(EmailLinkErrorCodeStatus.Failed);
    }
  };

  React.useEffect(() => {
    void startVerification();
  }, []);

  return (
    <EmailLinkStatusCard
      title={props.texts[verificationStatus]?.title || ''}
      subtitle={props.texts[verificationStatus]?.subtitle || ''}
      status={verificationStatus}
    />
  );
};
